"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTrackEvent } from "@/hooks/useTrackEvent";

type MobileStickyCTAProps = {
  ctaLabel: string;
  ctaHref: string;
  storageKey: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  eventProperties?: Record<string, unknown>;
  secondaryEventProperties?: Record<string, unknown>;
};

const BASE_OFFSET_PX = 16;

export function MobileStickyCTA({
  ctaLabel,
  ctaHref,
  storageKey,
  secondaryLabel,
  secondaryHref,
  eventProperties,
  secondaryEventProperties,
}: MobileStickyCTAProps) {
  const [visible, setVisible] = useState(false);
  const [cookieOffset, setCookieOffset] = useState(0);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    const hasClosed = sessionStorage.getItem(storageKey);
    if (hasClosed) return;
    const timeout = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timeout);
  }, [storageKey]);

  useEffect(() => {
    const banner = document.querySelector<HTMLElement>("[data-cookie-banner]");
    if (!banner) return;

    const updateOffset = () => {
      const isVisible = banner.offsetParent !== null;
      const height = isVisible ? banner.getBoundingClientRect().height : 0;
      setCookieOffset(height ? height + 12 : 0);
    };

    updateOffset();

    const mutationObserver = new MutationObserver(updateOffset);
    mutationObserver.observe(banner, { attributes: true, attributeFilter: ["style", "class"] });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateOffset);
      resizeObserver.observe(banner);
    }

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  function closeBar() {
    sessionStorage.setItem(storageKey, "dismissed");
    setVisible(false);
  }

  if (!visible || cookieOffset > 0) return null;

  return (
    <div
      className="fixed inset-x-0 z-40 px-4 md:hidden"
      style={{ bottom: `${BASE_OFFSET_PX + cookieOffset}px` }}
    >
      <div className="relative rounded-3xl border border-emerald/30 bg-gradient-to-r from-emerald/20 to-emerald/40 p-0.5 shadow-[0_15px_60px_rgba(3,6,20,0.6)] backdrop-blur">
        <button
          type="button"
          onClick={closeBar}
          className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full border border-border-strong bg-surface/80 text-xs text-foreground/70"
          aria-label="Dismiss"
        >
          ✕
        </button>
        <div className="flex flex-col items-center gap-2 rounded-[26px] bg-surface/70 px-3 py-3">
          <Link
            href={ctaHref}
            prefetch={ctaHref.startsWith("/")}
            className="flex w-full items-center justify-center rounded-full bg-emerald px-6 py-3 text-sm font-semibold text-midnight"
            onClick={() => trackEvent("book_button_mobile_sticky_click", { href: ctaHref, ...eventProperties })}
          >
            {ctaLabel}
          </Link>
          {secondaryLabel && secondaryHref ? (
            <Link
              href={secondaryHref}
              prefetch={secondaryHref.startsWith("/")}
              className="text-xs font-semibold uppercase tracking-[0.3em] text-offwhite/70 transition hover:text-offwhite"
              onClick={() =>
                trackEvent("cta_mobile_sticky_secondary_click", { href: secondaryHref, ...secondaryEventProperties })
              }
            >
              {secondaryLabel}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
